import type { CampaignResponse } from '@/api/campaignPlaner/fetchCampaign';
import { validateCampaignDateParams } from '@/dashboards/utils/validateDateAndPeriodSearchParams';
import type { OptimisationBase } from '@/optimizer';
import { DateFormat, formatDate } from '@/utils/dateUtils';
import type { ChannelInput, OptimisationInputs } from '@/dashboards/shared/optimisation/optimisationSession';

export type CampaignSearch = {
    budget?: number;
    startDate?: string;
    endDate?: string;
    optimizationBase?: OptimisationBase;
};

export type CampaignDefaults = {
    campaignDefaultBudget?: number | null;
    campaignDefaultStartDate?: Date | string | null;
    campaignDefaultEndDate?: Date | string | null;
};

export type ResolvedInitialInputs = {
    inputs: Omit<OptimisationInputs, 'budget'> & { budget: number | null };
    urlPatch: CampaignSearch | null;
};

const toUrlDate = (value: Date | string | null | undefined): string | undefined =>
    value ? formatDate(value, DateFormat.URL) : undefined;

export const lockedChannelsFromCampaign = (campaign: CampaignResponse): Record<string, ChannelInput> =>
    Object.fromEntries(
        [...(campaign.campaignStateRules ?? []), ...(campaign.customerRules ?? [])]
            .filter((rule) => rule.type === 'Total budget' && rule.subtype === 'is exactly')
            .map((rule) => [rule.channel, { locked: true, investment: Number(rule.value) }]),
    );

export const resolveInitialInputs = ({
    search,
    campaign,
    defaults,
    minDate,
    maxDate,
}: {
    search: CampaignSearch;
    campaign: CampaignResponse;
    defaults: CampaignDefaults;
    minDate: string;
    maxDate: string;
}): ResolvedInitialInputs => {
    const corrected = validateCampaignDateParams({
        boundaryStartDate: minDate,
        boundaryEndDate: maxDate,
        inputStartDate: search.startDate,
        inputEndDate: search.endDate,
        defaultStartDate: toUrlDate(campaign.startDate) ?? toUrlDate(defaults.campaignDefaultStartDate),
        defaultEndDate: toUrlDate(campaign.endDate) ?? toUrlDate(defaults.campaignDefaultEndDate),
    });
    const startDate = corrected?.startDate ?? search.startDate ?? minDate;
    const endDate = corrected?.endDate ?? search.endDate ?? maxDate;
    const budget = (search.budget || null) ?? (campaign.budget || null) ?? (defaults.campaignDefaultBudget || null);
    const optimizationBase: OptimisationBase = search.optimizationBase ?? campaign.optimizationBase ?? 'budget';

    const urlPatch: CampaignSearch = {};
    if (startDate !== search.startDate) urlPatch.startDate = startDate;
    if (endDate !== search.endDate) urlPatch.endDate = endDate;
    if (optimizationBase !== search.optimizationBase) urlPatch.optimizationBase = optimizationBase;
    if (budget !== null && budget !== search.budget) urlPatch.budget = budget;

    return {
        inputs: { budget, optimizationBase, startDate, endDate, channels: lockedChannelsFromCampaign(campaign) },
        urlPatch: Object.keys(urlPatch).length > 0 ? urlPatch : null,
    };
};
